<script setup>
    import { inject, onBeforeMount, computed, ref } from 'vue';
    import PlaylistService from '@services/playlists.js';
    import { usePlaylistsStore } from '@stores/playlists';
    import {updatePlaylistItem} from 'musora-content-services';
    import InputLabel from '@units/InputLabel/InputLabel.vue';
    import MuButton from '@units/Button/MuButton';

    //Emits
    const emit = defineEmits(['onCloseModal']);

    //Inject
    const token = inject('csrf_token');

    //Pinia Stores
    const playlistsStore = usePlaylistsStore();

    //-----------Props-----------//
    const props = defineProps({
        brand: {
            type: String,
            default: 'drumeo'
        },
        content: {
            type: Object,
            default: {}
        }
    });

    //--------Refs--------//

    const itemName = ref(props.content.playlist_item_name ? props.content.playlist_item_name : props.content.title);
    const nameKey = ref(props.content.playlist_item_name ? props.content.playlist_item_name : props.content.title);

    //--------Computed--------//

    const authors = computed(() => {
        if (props.content.artist && props.content.artist.length) {
            return props.content.artist;
        }

        let authors = [];
        if (props.content.instructors) {
            props.content.instructors.forEach((instructor) => {
                authors.push(instructor);
            });
        }
        return authors.join(', ');
    });

    //-----------Methods-----------//

    const handleConfirm = () => {
        updatePlaylistItem({
            user_playlist_item_id: props.content.user_playlist_item_id,
            playlist_item_name: itemName.value,
        }).then(() => {
                window.shownotification({
                    icon: 'edit',
                    text: 'Playlist item name has been successfully edited.'
                });
                playlistsStore.updatePlaylistItem({
                    ...props.content,
                    playlist_item_name: itemName.value,
                });
                emit('onCloseModal');
            })
            .catch(() => {
                window.shownotification({
                    icon: 'error',
                    text: 'There was an error saving your changes, try again later.'
                });
            });
    };

    const handleReset = () => {
        const name = itemName.value;
        itemName.value = props.content.playlist_item_name;
        nameKey.value = name;
    };

    const handleNameChange = val => {
        itemName.value = val;
    };
</script>
<template>
    <div class="tw-h-full tw-w-full">
        <div class="tw-flex tw-pb-4 tw-h-[80px] tw-items-center tw-justify-center">

            <!-- Playlist Image -->
            <div class="tw-relative tw-inline-flex tw-overflow-hidden tw-bg-white dark:tw-bg-[#081825] tw-aspect-video tw-w-[110px] tw-rounded tw-shrink-0">
                <!-- Image Conatiner -->
                <div class="tw-relative tw-w-full tw-h-full" v-if="content.thumbnail_url">
                    <img :src="`https://musora.com/cdn-cgi/image/width=330/${content.thumbnail_url}`" alt="playlist thumbnail"
                        class="tw-transition-opacity tw-opacity-0 tw-duration-500 tw-object-cover tw-object-center tw-w-full tw-h-full tw-blur-sm"
                        loading="lazy" onload="this.classList.remove('tw-opacity-0')" />
                    <!-- Image Mask -->
                    <div
                        class="tw-z-10 tw-absolute tw-w-full tw-h-full tw-left-0 tw-top-0 tw-bg-black/70 tw-flex tw-justify-center">
                        <img class="tw-h-full tw-object-contain"
                            :src="`https://musora.com/cdn-cgi/image/width=330/${content.thumbnail_url}`" alt="playlist thumbnail">
                    </div>
                </div>
            </div>

            <div class="tw-ml-[15px] tw-h-full tw-flex tw-flex-col tw-justify-center">
                <h3 class="tw-text-black dark:tw-text-white tw-text-[16px] tw-font-bold tw-font-open-sans">
                    {{ props.content.playlist_item_name ? props.content.playlist_item_name : props.content.title }}
                </h3>
                <p class="tw-text-black dark:tw-text-[#9EC0DC]">{{ authors }}</p>
            </div>
        </div>

        <div class="tw-flex tw-mt-[16px]">
            <InputLabel
                :initialValue="itemName"
                :key="nameKey"
                inputOverride="tw-w-full tw-h-[42px] tw-text-[#00101D]"
                :brand="brand"
                inputType="text"
                id="newItemName"
                inputName="newItemName"
                labelValue="New Name"
                placeholder="Enter a name for your item..."
                :inputErrors="[]"
                @onChange="handleNameChange"
            />
            <div class="tw-flex tw-flex-col tw-justify-end">
                <button @click.prevent="handleReset" class="tw-flex tw-items-center tw-justify-center tw-rounded-full tw-ml-[8px] tw-bg-white tw-text-black tw-h-[42px] tw-w-[42px]">
                    <svg width="23" height="22" viewBox="0 0 23 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4.2713 3.14273C4.2713 2.79558 3.98986 2.51416 3.64273 2.51416C3.29561 2.51416 3.01416 2.79558 3.01416 3.14273V7.72607C3.01416 8.07321 3.29561 8.35463 3.64273 8.35463H8.22604C8.57324 8.35463 8.85461 8.07321 8.85461 7.72607C8.85461 7.37891 8.57324 7.0975 8.22604 7.0975H5.27401C6.48703 5.10324 8.68082 3.7713 11.1856 3.7713C15.0042 3.7713 18.0999 6.86693 18.0999 10.6856C18.0999 14.5042 15.0042 17.5999 11.1856 17.5999C8.64184 17.5999 6.4189 16.2262 5.21815 14.1802C5.09354 13.9678 4.87033 13.8284 4.62411 13.8284C4.15621 13.8284 3.84215 14.3044 4.07303 14.7114C5.47719 17.1868 8.13634 18.857 11.1856 18.857C15.6985 18.857 19.357 15.1985 19.357 10.6856C19.357 6.17263 15.6985 2.51416 11.1856 2.51416C8.2743 2.51416 5.71866 4.03661 4.2713 6.32876V3.14273Z" fill="#18181B"/>
                    </svg>
                </button>
            </div>
        </div>
        <div class="tw-pt-[25px] tw-flex tw-justify-end tw-w-full">
            <!-- Close Modal -->
            <MuButton @click="() => emit('onCloseModal')" variant="secondary" class="tw-mr-[10px]">Cancel</MuButton>
            <!-- Delete Lesson -->
            <MuButton @click.prevent="handleConfirm()">RENAME ITEM</MuButton>
        </div>
    </div>
</template>
<style>
    input[type=range]::-webkit-slider-thumb {
        z-index: 2;
        position: relative;
    }
</style>
